/*
  Skin quiz.

  Opens on an intro panel and only reveals the questions once the visitor opts in.
  Every question and outcome is already in the DOM; this toggles which panel shows
  and tallies votes. Nothing is fetched until a routine is added, and the result
  appears before any email is asked for.
*/
class SkinQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.panels = Array.from(this.querySelectorAll('[data-quiz-result-panel]'));
    if (!this.steps.length || !this.panels.length) return;

    this.intro = this.querySelector('[data-quiz-intro]');
    this.stage = this.querySelector('[data-quiz-stage]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.progressLabel = this.querySelector('[data-quiz-progress-label]');
    this.segments = Array.from(this.querySelectorAll('[data-quiz-segment]'));
    this.progressTemplate = this.dataset.progressTemplate || '';
    this.miniCart = document.querySelector('mini-cart');
    this.backButtons = Array.from(this.querySelectorAll('[data-quiz-back]'));
    this.resultLabel = this.dataset.resultLabel || '';

    this.answers = [];
    // Runde 28: die gewählte Antwort je Frage; nach „Zurück“ steht sie markiert.
    this.choices = [];
    this.index = 0;

    this.addEventListener('click', this.onClick.bind(this));
    this.restoreResult();
  }

  static reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  static get storageKey() {
    return 'hashifit-quiz-result';
  }

  onClick(event) {
    const hit = (selector) => {
      const el = event.target.closest(selector);
      return el && this.contains(el) ? el : null;
    };

    if (hit('[data-quiz-start]')) return this.beginQuiz();
    if (hit('[data-quiz-restart]')) return this.beginQuiz();
    if (hit('[data-quiz-back]')) return this.goBack();

    const option = hit('[data-quiz-option]');
    if (option) return this.answer(option);

    const add = hit('[data-quiz-add]');
    if (add) return this.addRoutine(add);
  }

  /*
    Height is pinned before the swap and released after it, so a short question
    giving way to a tall result eases open instead of snapping the page down.
  */
  transition(mutate) {
    if (SkinQuiz.reducedMotion() || !this.stage) {
      mutate();
      return;
    }

    const stage = this.stage;
    const from = stage.getBoundingClientRect().height;

    mutate();

    /*
      The target is measured the same way as `from`, on the element in the state
      it will actually be released into — no is-measuring class, height auto.
      scrollHeight is no substitute: it rounds to an integer where
      getBoundingClientRect does not, and under overflow:hidden the stage is a
      block formatting context that stops child margins collapsing out — either
      leaves the pinned height a few pixels off the natural one, which shows as
      a jump the moment it is released.
    */
    stage.style.height = 'auto';
    const to = stage.getBoundingClientRect().height;

    if (Math.abs(to - from) < 1) {
      // Same height either way; animating it only risks a flicker.
      stage.style.height = '';
      return;
    }

    stage.classList.add('is-measuring');
    stage.style.height = `${from}px`;
    // Commit the start value, or the browser coalesces both writes and no
    // transition runs at all.
    void stage.getBoundingClientRect();

    requestAnimationFrame(() => {
      stage.style.height = `${to}px`;
    });

    this.releaseHeight(stage);
  }

  // Released when the height transition finishes — a fixed timeout can fire
  // while the easing is still running.
  releaseHeight(stage) {
    if (this.onHeightEnd) stage.removeEventListener('transitionend', this.onHeightEnd);
    clearTimeout(this.releaseTimer);

    this.onHeightEnd = (event) => {
      if (event.target !== stage || event.propertyName !== 'height') return;
      stage.style.height = '';
      stage.classList.remove('is-measuring');
      stage.removeEventListener('transitionend', this.onHeightEnd);
      this.onHeightEnd = null;
      clearTimeout(this.releaseTimer);
    };
    stage.addEventListener('transitionend', this.onHeightEnd);

    // Safety net: transitionend never fires if the transition is interrupted or
    // the tab is backgrounded mid-flight.
    this.releaseTimer = setTimeout(() => {
      if (this.onHeightEnd) this.onHeightEnd({ target: stage, propertyName: 'height' });
    }, 900);
  }

  // Only the visible panel carries is-active, which is what drives the stagger.
  show(panel) {
    const all = [this.intro, ...this.steps, ...this.panels].filter(Boolean);
    all.forEach((el) => {
      el.hidden = el !== panel;
      el.classList.toggle('is-active', el === panel);
    });
  }

  focusPanel(panel, selector) {
    const target = panel && panel.querySelector(selector);
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    // Runde 25: nach dem Schrittwechsel lag die Frage unter dem Sticky-Header
    // (top −70 px bei 64 px Header). Liegt sie außerhalb des sichtbaren Bereichs,
    // wird sie eingescrollt; scroll-margin-top (CSS) hält den Header frei.
    // Runde 27: bei 375 lag die Frage nach dem Start trotzdem 77 px über dem
    // Sichtbereich, weil die Intro-Karte höher war als die Frage und der
    // Scrollstand vom Startknopf blieb. Position aus der Sektion berechnen und
    // nach dem Umbau der Bühne noch einmal nachziehen.
    // Runde 28: Anker ist der Fortschritt über der Bühne („Frage 1 von 6“,
    // Balken, Zurück), nicht die Frage: mit der Frage als Anker lag die Zeile
    // bei 375 px unter dem Header (top 21 bei 62 px Header). Reserviert wird
    // die volle Headerhöhe, weil der Header beim Hochscrollen wieder einblendet.
    const anchor = this.progress && !this.progress.hidden ? this.progress : target;
    const place = () => {
      if (!target.isConnected) return;
      const header = document.querySelector('.shopify-section-header, .header-wrapper, sticky-header, header.header');
      const headerSpace = header ? header.getBoundingClientRect().height : 0;
      const anchorTop = anchor.getBoundingClientRect().top;
      const targetBottom = target.getBoundingClientRect().bottom;
      if (anchorTop < headerSpace + 8 || targetBottom > window.innerHeight) {
        const top = Math.max(0, anchorTop + window.scrollY - headerSpace - 16);
        window.scrollTo({ top, behavior: SkinQuiz.reducedMotion() ? 'auto' : 'smooth' });
      }
    };
    place();
    window.setTimeout(place, 80);
    window.setTimeout(place, 450);
  }

  beginQuiz() {
    this.clearResult();
    this.answers = [];
    this.choices = [];
    this.steps.forEach((step) => this.markChoice(step, null));
    this.index = 0;
    if (this.progress) this.progress.hidden = false;
    this.transition(() => this.showStep());
  }

  answer(option) {
    // Stored per step so Back can undo one vote without replaying the quiz.
    this.answers[this.index] = parseInt(option.dataset.quizResult, 10) || 1;
    this.choices[this.index] = option;
    this.markChoice(this.steps[this.index], option);
    this.index += 1;
    this.transition(() => (this.index >= this.steps.length ? this.showResult() : this.showStep()));
  }

  goBack() {
    if (this.index === 0) return;
    if (this.index >= this.steps.length) this.clearResult();
    this.index -= 1;
    this.answers.length = this.index;
    // Spätere Antworten verfallen wie ihre Stimmen; die der Frage, zu der es
    // zurückgeht, bleibt markiert.
    this.choices.length = this.index + 1;
    this.transition(() => this.showStep());
  }

  showStep() {
    const current = this.steps[this.index];
    this.show(current);

    // Runde 28: Zurück steht oben im Fortschritt (ohne Fortschrittsanzeige
    // weiter unter den Antworten der jeweiligen Frage).
    this.backButtons.forEach((back) => {
      back.hidden = this.index === 0;
    });
    this.markChoice(current, this.choices[this.index]);

    this.focusPanel(current, '.skin-quiz__question');
    this.updateProgress(this.index + 1, this.steps.length);
  }

  markChoice(step, chosen) {
    if (!step) return;
    step.querySelectorAll('[data-quiz-option]').forEach((option) => {
      option.setAttribute('aria-pressed', option === chosen ? 'true' : 'false');
    });
  }

  showResult(options = {}) {
    const result = this.resolveResult();
    const panel = result.panel;
    this.show(panel);
    this.renderReason(panel, result);
    // Runde 28: aus dem Ergebnis führt Zurück zur letzten Frage; die Zeile
    // heißt „Dein Ergebnis“ statt weiter „Frage 6 von 6“.
    this.backButtons.forEach((back) => {
      back.hidden = false;
    });
    // Runde 30: ein gemerktes Ergebnis erscheint beim Laden ohne Fokus- und Scrollsprung.
    if (!options.restore) this.focusPanel(panel, '.skin-quiz__result-heading');
    this.updateProgress(this.steps.length, this.steps.length, this.resultLabel);
    this.saveResult();
  }

  /*
    Highest vote count wins. Ties fall to the lowest-numbered outcome so the same
    answers always produce the same routine — a random or last-wins tiebreak would
    make the quiz feel broken when a visitor retakes it.
    Runde 30: Liegen mehrere Schwerpunkte gleichauf vorn, gewinnt vorher ein Set,
    dessen combo genau diese Schwerpunkte verbindet (Tag & Nacht Duo bei 1 und 2)
    oder das selbst unter den Führenden steht (Balance Trio bei „mehrere Bereiche“
    und Tag, Abend oder Nerven). Passen mehrere Sets, gewinnt das kleinste.
  */
  resolveResult() {
    const tally = new Map();
    this.answers.forEach((result) => tally.set(result, (tally.get(result) || 0) + 1));

    const numberOf = (panel) => parseInt(panel.dataset.quizResultPanel, 10);
    const best = Math.max(0, ...tally.values());
    const leaders = this.panels.map(numberOf).filter((n) => best > 0 && (tally.get(n) || 0) === best);
    const panelFor = (n) => this.panels.find((panel) => numberOf(panel) === n);

    let combo = null;
    if (leaders.length > 1) {
      this.panels.forEach((panel) => {
        const parts = (panel.dataset.quizCombo || '').split('+').map((part) => parseInt(part, 10)).filter(Boolean);
        if (parts.length < 2) return;
        const own = numberOf(panel);
        const covered = leaders.every((n) => n === own || parts.includes(n));
        const anchored = leaders.includes(own) || parts.every((n) => leaders.includes(n));
        if (covered && anchored && (!combo || parts.length < combo.parts.length)) combo = { panel, parts };
      });
    }

    if (combo) return { panel: combo.panel, kind: 'combo', amount: best, leaders };
    if (leaders.length === 1) return { panel: panelFor(leaders[0]), kind: 'single', amount: best, leaders };
    if (leaders.length > 1) return { panel: panelFor(leaders[0]), kind: 'tie', amount: best, leaders };
    return { panel: this.panels[0], kind: 'none', amount: 0, leaders };
  }

  // Runde 30: ein Satz unter der Überschrift, auf welchen Antworten das Ergebnis
  // beruht („4 von 6 Antworten passen zu …“), ohne Wirkversprechen.
  renderReason(panel, result) {
    const target = panel.querySelector('[data-quiz-reason]');
    if (!target) return;

    const topicOf = (n) => {
      const source = this.panels.find((p) => parseInt(p.dataset.quizResultPanel, 10) === n);
      if (!source) return '';
      if (source.dataset.quizTopic) return source.dataset.quizTopic;
      const heading = source.querySelector('.skin-quiz__result-heading');
      return heading ? heading.textContent.split(':').pop().trim() : '';
    };
    const list = (items) => {
      if (items.length < 2) return items.join('');
      return `${items.slice(0, -1).join(', ')} ${this.dataset.reasonAnd || 'und'} ${items[items.length - 1]}`;
    };
    const fill = (template, values) =>
      Object.keys(values).reduce((text, key) => text.split(`[${key}]`).join(values[key]), template || '');

    const topics = result.leaders.map(topicOf).filter(Boolean).map((topic) => `„${topic}“`);
    const values = { amount: result.amount, total: this.answers.length };
    let text = '';
    if (result.kind === 'single' && topics.length) {
      text = fill(this.dataset.reasonSingle, { ...values, topic: topics[0] });
    } else if (result.kind === 'combo' && topics.length) {
      text = fill(this.dataset.reasonCombo, { ...values, topics: list(topics) });
    } else if (result.kind === 'tie' && topics.length > 1) {
      text = fill(this.dataset.reasonTie, { ...values, topic: topics[0], others: list(topics.slice(1)) });
    }

    target.textContent = text;
    target.hidden = !text;
  }

  /*
    Runde 30: Das Ergebnis bleibt für die Sitzung gemerkt (sessionStorage): Nach dem
    Neuladen oder dem Rückweg von einer Produktseite steht es wieder da. Gespeichert
    werden nur die gewählten Antworten je Frage; geändert sich das Quiz im Editor,
    passt der Stand nicht mehr und wird verworfen.
  */
  saveResult() {
    try {
      const choices = this.steps.map((step, i) => Array.from(step.querySelectorAll('[data-quiz-option]')).indexOf(this.choices[i]));
      sessionStorage.setItem(SkinQuiz.storageKey, JSON.stringify({ count: this.steps.length, choices }));
    } catch (error) {
      // Ohne Speicher (privates Fenster, gesperrt) bleibt alles wie vorher.
    }
  }

  clearResult() {
    try {
      sessionStorage.removeItem(SkinQuiz.storageKey);
    } catch (error) {}
  }

  restoreResult() {
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(SkinQuiz.storageKey) || 'null');
    } catch (error) {
      return;
    }
    if (!stored || stored.count !== this.steps.length || !Array.isArray(stored.choices)) return;

    const choices = this.steps.map((step, i) => step.querySelectorAll('[data-quiz-option]')[stored.choices[i]] || null);
    if (choices.some((option) => !option)) {
      this.clearResult();
      return;
    }

    this.choices = choices;
    this.answers = choices.map((option) => parseInt(option.dataset.quizResult, 10) || 1);
    this.steps.forEach((step, i) => this.markChoice(step, choices[i]));
    this.index = this.steps.length;
    if (this.progress) this.progress.hidden = false;
    this.showResult({ restore: true });
  }

  updateProgress(current, total, label) {
    // Segments fill up to and including the current step, so the visitor always
    // sees how many questions remain rather than an unmarked percentage.
    this.segments.forEach((segment, i) => {
      segment.classList.toggle('is-filled', i < current);
    });
    if (this.progressLabel && label) {
      this.progressLabel.textContent = label;
    } else if (this.progressLabel && this.progressTemplate) {
      this.progressLabel.textContent = this.progressTemplate
        .replace('[current]', current)
        .replace('[total]', total);
    }
  }

  addRoutine(button) {
    const ids = (button.dataset.variantIds || '').split(',').filter(Boolean);
    if (!ids.length) return;

    const items = { items: ids.map((id) => ({ id: id, quantity: 1 })) };
    const errorTarget = button.closest('[data-quiz-result-panel]').querySelector('[data-quiz-error]');
    this.setError(errorTarget, false);

    // Same two paths BundleProducts uses: a real form post when the drawer is off
    // or we are already on the cart page, otherwise the AJAX cart.
    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      Shopify.postLink2(theme.routes.cart_add_url, { parameters: { ...items } });
      return;
    }

    button.setAttribute('disabled', true);
    button.classList.add('loading');

    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];
    const body = JSON.stringify({
      ...items,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setError(errorTarget, response.description);
          return;
        }

        this.miniCart && this.miniCart.renderContents(response);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: response } }));
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('disabled');
      });
  }

  setError(target, message) {
    if (!target) {
      if (message) alert(message);
      return;
    }
    target.textContent = message || '';
    target.hidden = !message;
  }
}

if (!customElements.get('skin-quiz')) {
  customElements.define('skin-quiz', SkinQuiz);
}
(() => {
  const updateQuizLinks = (root = document) => {
    root.querySelectorAll("skin-quiz [data-quiz-add]").forEach((button) => {
      const panel = button.closest("[data-quiz-result-panel]");
      const productLink = panel?.querySelector("a.skin-quiz__step-name");
      if (!productLink) return;

      const link = document.createElement("a");
      link.href = productLink.href;
      link.className = button.className;
      // Runde 30: auch die Sets mit combo (Duos) heißen „Kombination kennenlernen“
      link.textContent = panel.dataset.quizResultPanel === "6" || panel.dataset.quizCombo
        ? "Kombination kennenlernen"
        : "Produkt kennenlernen";
      button.replaceWith(link);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => updateQuizLinks(), { once: true });
  } else {
    updateQuizLinks();
  }

  document.addEventListener("shopify:section:load", (event) => updateQuizLinks(event.target));
})();
